import type { ChapterData } from "./content-types";

/**
 * CAPÍTULO 3 — "La botella sin nombre" (Laboratorios Nova).
 * Mecánica distintiva: CLASIFICAR productos por su pictograma GHS
 * para separar incompatibles (extra.type: "classify").
 */
export const LABO: ChapterData = {
  id: "labo",
  title: "Laboratorios Nova",
  location: "labo",
  secStart: 40,
  secGreat: 82,
  secOk: 66,
  secBad: 56,
  needTalk: 2,
  needHazards: 3,

  interactables: [
    { id: "estanteria", type: "machine", pos: { x: 11, y: 3 }, label: "Estantería 03", verb: "use", ref: "estanteria", radius: 1.7, color: 0xc7a06a },
    { id: "vitrina", type: "object", pos: { x: 6, y: 3 }, label: "Vitrina de extracción", verb: "inspect", ref: "vitrina", radius: 1.5, color: 0x8aa3a0 },
    { id: "botella", type: "object", pos: { x: 3, y: 4 }, label: "Botella sin nombre", verb: "inspect", ref: "botella", radius: 1.3, color: 0xb7d8d2 },
    { id: "fichas", type: "document", pos: { x: 2, y: 6 }, label: "Carpeta de fichas (FDS)", verb: "read", ref: "fichas", radius: 1.4, color: 0x3a6a86 },
    { id: "guantes", type: "object", pos: { x: 8, y: 6 }, label: "Guantes descartados", verb: "inspect", ref: "guantes", radius: 1.3, color: 0x7fb0d8 },
    { id: "lavaojos", type: "object", pos: { x: 13, y: 6 }, label: "Lavaojos / ducha", verb: "inspect", ref: "lavaojos", radius: 1.4, color: 0x2f8f57 },
    { id: "derrame", type: "machine", pos: { x: 6, y: 6 }, label: "Derrame", verb: "use", ref: "derrame", radius: 1.5, color: 0xc7d94a },
    { id: "nuria", type: "npc", pos: { x: 4, y: 8 }, label: "Nuria", verb: "talk", ref: "nuria", radius: 1.5, color: 0xf0c9a0 },
    { id: "diego", type: "npc", pos: { x: 8, y: 8 }, label: "Wei", verb: "talk", ref: "diego", radius: 1.5, color: 0xc98b4a },
    { id: "vega", type: "npc", pos: { x: 12, y: 8 }, label: "Vega", verb: "talk", ref: "vega", radius: 1.5, color: 0x2f2f36 },
    { id: "taquilla", type: "locker", pos: { x: 2, y: 9 }, label: "Taquilla de EPIs", verb: "open", radius: 1.6 },
    { id: "salida", type: "door", pos: { x: 13, y: 10 }, label: "Salir a la ciudad", verb: "open", radius: 1.7 },
  ],

  hotspots: {
    estanteria: {
      label: "Estantería 03",
      text: "La <b>estantería 03</b>. En el mismo estante conviven un <b>inflamable</b> y un <b>comburente</b>: son <b>incompatibles</b>. Juntos, cualquier chispa es una fiesta que no quieres.",
      note: { section: "pistas", heading: "Incompatibles juntos (estante 03)", body: "Un inflamable y un comburente compartiendo estante. Deben almacenarse separados por familias según su pictograma." },
      hazardId: "h_incompat",
      comment: "Inflamable y comburente, codo con codo. El que los colocó ahí no miró un solo pictograma.",
    },
    vitrina: {
      label: "Vitrina de extracción",
      text: "La <b>vitrina de gases</b>. La extracción está <b>apagada</b> y la guillotina, subida. Sin extracción, los vapores del trasvase se quedan en el aire que respiras.",
      note: { section: "pistas", heading: "Extracción apagada", body: "La vitrina estaba apagada durante un trasvase. Los vapores no se retiraban." },
      hazardId: "h_extrac",
      comment: "Han apagado la extracción. Trasvasar así es respirar justo lo que no debes.",
      signs: ["m_mascarilla", "ghs_inflamable"],
      instrument: {
        device: "i_gasdetector",
        button: "Medir vapores",
        kind: "gauge",
        from: 20,
        to: 380,
        unit: "ppm",
        danger: true,
        reading: "380 ppm de vapor de disolvente en el aire, con la extracción apagada. Muy por encima de lo aceptable: hay que encender la vitrina antes de seguir y usar protección respiratoria.",
        note: { section: "pistas", heading: "Medición: 380 ppm de vapores", body: "El detector marca 380 ppm de vapores con la extracción apagada. Nivel inaceptable: encender la vitrina y proteger la respiración." },
      },
      assess: {
        identify: {
          q: "¿Qué riesgo hay al trasvasar con la extracción apagada?",
          options: [
            { text: "Inhalación de vapores tóxicos/inflamables que se acumulan en el aire", ok: true },
            { text: "Contacto eléctrico con la vitrina", ok: false },
            { text: "Sobreesfuerzo al levantar el envase", ok: false },
          ],
          hint: "El detector marca 380 ppm: sin extracción, los vapores del disolvente se quedan en el aire que respiras.",
        },
        control: {
          q: "¿Cuál es la mejor medida, según la jerarquía preventiva?",
          options: [
            { text: "Encender la extracción y trabajar con la guillotina baja", tier: "colectiva", grade: "best", why: "La vitrina retira los vapores en su origen para todo el laboratorio: es la barrera principal y va antes que la mascarilla." },
            { text: "Ponerte mascarilla con filtro y trasvasar sin encender la vitrina", tier: "epi", grade: "ok", why: "La protección respiratoria ayuda, pero es el último recurso: sin extracción el aire sigue cargado para todos." },
            { text: "Abrir una ventana y trasvasar rápido", tier: "ninguna", grade: "bad", why: "Una corriente de aire no dirige los vapores fuera de tu cara: no controla el riesgo.", danger: "El vapor concentrado te hace toser y arder la garganta; con un inflamable, además, cualquier chispa es un incendio." },
          ],
        },
        apply: {
          intro: "Trasvasa con la extracción funcionando. Haz los pasos en orden.",
          steps: [
            "Comprobar que la vitrina está libre y limpia",
            "Encender la extracción y esperar a que haya flujo",
            "Bajar la guillotina a la altura de trabajo",
            "Hacer el trasvase dentro de la vitrina",
          ],
          wrong: "Ese paso todavía no. La extracción debe estar en marcha antes de bajar la guillotina y trasvasar.",
          success: "Trasvase hecho con la extracción retirando los vapores: aire limpio.",
        },
        note: { section: "expedientes", heading: "Extracción antes que mascarilla", body: "En trasvases de productos volátiles, la vitrina de extracción (protección colectiva) retira los vapores en origen. La mascarilla es el último recurso." },
      },
    },
    botella: {
      label: "Botella sin nombre",
      text: "Una botella con la <b>etiqueta arrancada</b>. Sin etiqueta no hay pictograma, no hay ficha, no hay forma de saber cómo tratarla.",
      note: { section: "objetos", heading: "Botella sin etiqueta", body: "Un envase sin identificar. Todo producto debe estar etiquetado: sin etiqueta no se sabe el riesgo." },
      comment: "Una botella sin nombre en un laboratorio. Como una nota anónima: nunca trae nada bueno.",
      signs: ["ghs_inflamable", "ghs_corrosivo", "ghs_toxico"],
    },
    fichas: {
      label: "Carpeta de fichas (FDS)",
      text: "La carpeta de <b>fichas de seguridad</b>. Justo la del producto del trasvase <b>falta</b>. La ficha dice pictograma, EPI, incompatibilidades y qué hacer si se derrama.",
      note: { section: "expedientes", heading: "Falta la ficha de seguridad", body: "La FDS del producto implicado no está. Sin ficha se trabaja a ciegas." },
      giveItem: "ficha_labo",
      comment: "La ficha del producto justo no está. Toda la información de seguridad, evaporada.",
    },
    guantes: {
      label: "Guantes descartados",
      text: "Unos guantes tirados, <b>medio deshechos</b> por el producto. Eran de un material que no resistía. El guante correcto se elige según la ficha.",
      note: { section: "objetos", heading: "Guantes inadecuados", body: "Los guantes usados no resistían el producto. El material del guante debe elegirse según la ficha." },
      hazardId: "h_guantes",
      comment: "Estos guantes se han deshecho solos. No todo guante vale para todo producto.",
      signs: ["m_guantes", "ghs_corrosivo"],
      assess: {
        identify: {
          q: "¿Qué riesgo revelan estos guantes deshechos?",
          options: [
            { text: "Contacto de la piel con el producto por un guante inadecuado", ok: true },
            { text: "Riesgo de atrapamiento", ok: false },
            { text: "Riesgo por ruido", ok: false },
          ],
          hint: "El producto ha degradado el propio guante. Piensa qué le pasa a la piel si el guante no resiste al químico.",
        },
        control: {
          q: "¿Cuál es la mejor medida, según la jerarquía preventiva?",
          options: [
            { text: "Consultar la ficha y usar el guante del material que resiste ese producto", tier: "epi", grade: "best", why: "Aquí el contacto no se puede eliminar, así que el EPI adecuado es la medida clave: el material (nitrilo, neopreno, butilo...) lo indica la ficha de seguridad." },
            { text: "Ponerte dos pares de látex para más protección", tier: "epi", grade: "ok", why: "Doblar guantes del material equivocado no sirve: se degradan igual. El problema no es el grosor, es el material." },
            { text: "Trabajar con cuidado sin guantes", tier: "ninguna", grade: "bad", why: "El contacto directo con la piel es justo lo que hay que evitar.", danger: "El producto te irrita y quema la piel de las manos nada más rozarla." },
          ],
        },
        apply: {
          intro: "Elige y ponte el guante correcto. Haz los pasos en orden.",
          steps: [
            "Consultar la ficha de seguridad del producto",
            "Elegir el guante del material que indica (nitrilo, neopreno...)",
            "Revisar que no tenga poros ni roturas",
            "Ponérselos y manipular el producto",
          ],
          wrong: "Ese paso todavía no. Primero la ficha dice qué material resiste; luego se elige y se revisa antes de usarlo.",
          success: "Guante del material correcto, revisado y puesto: la piel queda protegida.",
        },
        note: { section: "expedientes", heading: "El guante lo elige la ficha", body: "Cuando el contacto no puede eliminarse, el EPI correcto es imprescindible: el material del guante se elige según el producto y su ficha de seguridad." },
      },
    },
    lavaojos: {
      label: "Lavaojos / ducha",
      text: "La <b>ducha de emergencia y el lavaojos</b>... con unas cajas delante. En una salpicadura a los ojos, ese acceso debe estar libre.",
      note: { section: "pistas", heading: "Lavaojos bloqueado", body: "El acceso a la ducha/lavaojos estaba obstruido. Debe estar siempre despejado y señalizado." },
      hazardId: "h_lavaojos",
      comment: "El lavaojos, con cajas delante. Justo lo que corres a usar cuando algo salta a los ojos.",
      signs: ["e_lavaojos", "e_ducha"],
      assess: {
        identify: {
          q: "¿Qué riesgo supone el lavaojos bloqueado con cajas?",
          options: [
            { text: "No poder lavar a tiempo unos ojos salpicados por un químico", ok: true },
            { text: "Riesgo de incendio de las cajas", ok: false },
            { text: "Sobreesfuerzo al mover las cajas", ok: false },
          ],
          hint: "Es un medio de emergencia. Piensa qué pasa si alguien recibe una salpicadura y corre a usarlo... y está tapado.",
        },
        control: {
          q: "¿Cuál es la mejor medida, según la jerarquía preventiva?",
          options: [
            { text: "Retirar las cajas ahora y mantener el acceso siempre libre y señalizado", tier: "eliminar", grade: "best", why: "Despejar el acceso elimina el problema en el acto: en una salpicadura cuentan los segundos y el lavaojos debe estar siempre disponible." },
            { text: "Poner un cartel de 'no obstruir el lavaojos'", tier: "organizativa", grade: "ok", why: "Señalizar ayuda a que no se repita, pero si las cajas siguen ahí el acceso continúa bloqueado: primero despejarlo." },
            { text: "Apuntar en el parte que las quiten mañana", tier: "ninguna", grade: "bad", why: "Una emergencia no espera al parte de mañana.", danger: "Esa misma tarde, una salpicadura a los ojos y el lavaojos tapado: los segundos perdidos cuestan la vista." },
          ],
        },
        apply: {
          intro: "Deja el lavaojos operativo. Haz los pasos en orden.",
          steps: [
            "Retirar las cajas que bloquean el acceso",
            "Accionar el lavaojos y comprobar que sale agua",
            "Señalizar 'no obstruir' la zona",
            "Dejar el camino despejado y libre",
          ],
          wrong: "Ese paso todavía no. Primero se despeja y se comprueba que funciona; señalizar es después.",
          success: "Lavaojos despejado, probado y señalizado: listo para una emergencia.",
        },
        note: { section: "expedientes", heading: "Lavaojos siempre despejado", body: "La ducha de emergencia y el lavaojos deben tener el acceso libre y señalizado. Despejar el obstáculo es prioritario; señalizar es complementario." },
      },
    },
    derrame: {
      label: "Derrame",
      text: "Un <b>charco</b> de producto en el suelo, humeando. Nadie lo ha acotado ni neutralizado. Para limpiarlo hay que hacerlo <b>con seguridad</b>: extracción, EPI y método.",
      note: { section: "pistas", heading: "Derrame sin controlar", body: "Producto derramado sin acotar ni neutralizar. Cada ficha indica cómo actuar; aquí no se siguió." },
      comment: "Un charco humeante y nadie lo ha tocado. La ficha dice cómo limpiarlo; alguien no la leyó.",
    },
  },

  npcs: {
    nuria: {
      name: "Nuria",
      role: "Técnica de laboratorio",
      portrait: "nuria",
      note: { section: "personas", heading: "Nuria", body: "Técnica meticulosa. Avisó de que la estantería 03 mezclaba incompatibles." },
      dialogue: [
        { speaker: "npc", text: "Por fin alguien que mira los pictogramas. Yo lo dije por activa y por pasiva: en la 03 esos dos no pueden ir juntos." },
        { speaker: "epi", text: "Explícamelo como si no supiera de química. ¿Por qué es tan grave?" },
        { choices: [ { text: "¿Qué pasa si un inflamable y un comburente comparten estante?", go: 3 }, { text: "¿Y cómo acabaron juntos?", go: 4 } ] },
        { speaker: "npc", text: "El comburente aviva el fuego; el inflamable solo necesita una chispa. Juntos, cualquier tontería —una estática, un roce— y no hablamos de un susto, hablamos de la 03 ardiendo. Por eso cada familia va en su armario.", flag: "nuria_incompat", note: { section: "pistas", heading: "Incompatibles por falta de espacio", body: "Nuria avisó: un inflamable y un comburente juntos convierten cualquier chispa en incendio. Deben separarse por familias según pictograma." }, end: true },
        { speaker: "npc", text: "Por espacio, agárrate. 'Ponlos donde quepan', me dijeron. Como si un inflamable y un comburente fueran botes de mermelada. Hasta el armario de seguridad que separa las familias entró en la lista de recortes. Cada cosa en su sitio costaba dinero, así que fuera.", flag: "nuria_incompat", note: { section: "pistas", heading: "Incompatibles por falta de espacio", body: "Nuria avisó de la incompatibilidad. Los colocaron juntos 'donde cabían' tras recortar armarios de seguridad, ignorando el pictograma." }, end: true },
      ],
    },
    diego: {
      name: "Wei",
      role: "En prácticas",
      portrait: "diego",
      note: { section: "personas", heading: "Wei", body: "Becario con prisa. Hizo el trasvase sin ficha, con guantes cualquiera, y apagó la extracción." },
      dialogue: [
        { speaker: "npc", text: "Yo solo hice un trasvase rápido, ¿eh? Un momento y ya." },
        { speaker: "epi", text: "¿Miraste la ficha antes?" },
        { choices: [ { text: "¿Y la extracción, por qué está apagada?", go: 3 }, { text: "¿Con qué guantes lo hiciste?", go: 4 } ] },
        { speaker: "npc", text: "La apagué yo. Hacía un ruido que no me dejaba concentrarme. Iba a ser un momento... pero se derramó y empezó a oler fatal.", flag: "diego_extrac", note: { section: "pistas", heading: "Extracción apagada por el ruido", body: "Wei apagó la extracción porque 'hacía ruido' y trasvasó sin ella. Al derramarse, los vapores se acumularon." }, end: true },
        { speaker: "npc", text: "Pues los guantes que había por ahí. No miré la ficha. Se me empezaron a deshacer en las manos y ahí me asusté.", flag: "diego_guantes", note: { section: "pistas", heading: "Trasvase sin ficha ni guantes correctos", body: "Wei trasvasó sin consultar la ficha y con guantes inadecuados, que el producto degradó." }, end: true },
      ],
    },
    vega: {
      name: "Vega",
      role: "¿?",
      portrait: "vega",
      note: { section: "personas", heading: "Vega", body: "Vuelve a aparecer. Habla de una lista de números: 03, 07..." },
      dialogue: [
        { speaker: "npc", text: "Cero tres. Ya llevas unos cuantos, ¿verdad, EPI?" },
        { speaker: "epi", text: "Usted otra vez. ¿Qué significan los números?" },
        { speaker: "npc", text: "Cada uno marca un punto donde alguien decidió que la seguridad estorbaba. No son casualidades: son una lista. Cuando la tengas entera, sabrás quién la escribió.", flag: "vega_lista", note: { section: "pistas", heading: "La lista de Vega", body: "Vega confirma que los números señalan lugares donde se anuló la seguridad a propósito." }, end: true },
      ],
    },
  },

  hazards: [
    { id: "h_incompat", category: "Químico: incompatibles", severity: 3, discoverable: true, requiredAction: "Separar por familias según pictograma" },
    { id: "h_extrac", category: "Químico: vapores", severity: 3, discoverable: true, requiredAction: "Extracción siempre en trasvases", relatedEquipment: ["mascarilla"] },
    { id: "h_guantes", category: "Químico: contacto", severity: 2, discoverable: true, requiredAction: "Guantes según la ficha", relatedEquipment: ["guantes"] },
    { id: "h_lavaojos", category: "Emergencia bloqueada", severity: 2, discoverable: true, requiredAction: "Despejar el lavaojos" },
  ],

  mission: {
    id: "cap3_botella",
    title: "La botella sin nombre",
    location: "labo",
    objectives: [
      { id: "talk", type: "talk_workers", label: "Habla con la gente del laboratorio", target: "2" },
      { id: "hazards", type: "discover_hazards", label: "Detecta los riesgos químicos", target: "3" },
      { id: "extra", type: "extra", label: "Reorganiza la estantería 03", hidden: true },
      { id: "equip", type: "equip", label: "Prepárate según la ficha", hidden: true },
      { id: "report", type: "report", label: "Limpia el derrame con seguridad", hidden: true },
    ],
  },

  epi: {
    needed: ["guantes", "mascarilla", "gafas"],
    optional: ["botas"],
    overkill: ["casco", "arnes", "auditiva", "chaleco"],
  },

  extra: {
    triggerId: "estanteria",
    type: "classify",
    signs: ["ghs_inflamable", "ghs_comburente", "ghs_corrosivo", "ghs_toxico"],
    label: "Reorganizar la estantería 03",
    families: [
      { key: "inflamable", label: "Inflamables" },
      { key: "comburente", label: "Comburentes" },
      { key: "corrosivo", label: "Corrosivos" },
      { key: "toxico", label: "Tóxicos" },
    ],
    products: [
      { id: "acetona", name: "Acetona", picto: "inflamable", fam: "inflamable", hint: "Ficha: líquido y vapores muy inflamables." },
      { id: "peroxido", name: "Peróxido concentrado", picto: "comburente", fam: "comburente", hint: "Ficha: puede avivar el fuego; comburente." },
      { id: "acido", name: "Ácido clorhídrico", picto: "corrosivo", fam: "corrosivo", hint: "Ficha: provoca quemaduras; corrosivo." },
      { id: "amoniaco", name: "Amoníaco", picto: "toxico", fam: "toxico", hint: "Ficha: tóxico por inhalación." },
    ],
    wrong: "Alguna clasificación no encaja con su pictograma. La llama es inflamable; la llama sobre el círculo, comburente; la superficie atacada, corrosivo; la calavera, tóxico.",
    success: "Cada producto en su armario, por familias. El inflamable y el comburente ya no comparten estante: se acabó la combinación peligrosa de la 03.",
    note: { section: "expedientes", heading: "Almacenamiento por familias", body: "Los productos se separan según su pictograma: inflamables, comburentes, corrosivos y tóxicos en armarios distintos. Nunca juntar incompatibles." },
  },

  consequence: {
    title: "Vuelves al laboratorio",
    lines: {
      guantes: "Sin guantes adecuados, el producto te irrita la piel de las manos nada más rozarla.",
      mascarilla: "Sin protección respiratoria, un vaho del trasvase te hace toser y arder la garganta.",
      gafas: "Sin gafas, una salpicadura salta cerca de tus ojos. Menos mal que el lavaojos ya está despejado.",
    },
    great: "Con la extracción en marcha, guantes químicos, gafas y mascarilla, la limpieza del derrame se hace sin exponerte a nada.",
    greatOver: "Casco y arnés en un laboratorio... 'no vamos a escalar el armario de reactivos.'",
    ok: "Sale, pero te has llevado alguna irritación o vaho que, con la ficha delante, no tocaba.",
    bad: "Meterse con los productos sin guantes, gafas ni protección respiratoria es justo lo que investigabas.",
  },

  workId: "derrame",
  reward: "tarjeta11",
  resolveNotes: [
    { section: "pistas", heading: "Tarjeta 11", body: "Junto a la botella ya etiquetada. La lista de Vega: 03, 07, 11." },
    { section: "expedientes", heading: "Caso: La botella sin nombre", body: "Exposición química por trasvase sin ficha, incompatibles juntos y extracción apagada. Resuelto. Cabo suelto: la tarjeta 11." },
  ],
  resolveText:
    `<p>Con la extracción en marcha, los incompatibles separados y el equipo puesto, <b>acotas y neutralizas el derrame</b> siguiendo la ficha, y despejas el lavaojos. El laboratorio respira.</p>
     <div class="epi-callout">Sobre la mesa, junto a la botella ya etiquetada, aparece otra tarjeta: <b>11</b>.<br>“Cero tres, cero siete, once... alguien lleva la cuenta.”</div>`,
};
